<template>
  <button
    type="button"
    :disabled="disabled"
    :data-active-tab="isActive ? 'true' : null"
    :class="tabClass"
    @click="handleClick"
  >
    <span><slot /></span>
  </button>
</template>

<script setup lang="ts">
import { inject } from 'vue'
import { TabListContextKey, TabsContextKey } from './useTabsContext'

type TabSize = 'medium' | 'large'

const props = withDefaults(
  defineProps<{
    active?: boolean
    size?: TabSize
    value?: string
    disabled?: boolean
    customClass?: string
  }>(),
  {
    active: undefined,
    size: 'medium',
    customClass: ''
  }
)

const tabsContext = inject(TabsContextKey, null)
const tabListContext = inject(TabListContextKey, null)

const isActive = computed(() => props.active ?? (tabsContext ? tabsContext.value.value === props.value : false))

const handleClick = () => {
  if (tabsContext && props.value && !props.disabled) {
    tabsContext.onValueChange(props.value)
  }
}

const tabBaseClass =
  'tw:relative tw:inline-flex tw:items-center tw:justify-center tw:gap-1.5 tw:px-3 tw:whitespace-nowrap tw:select-none tw:cursor-pointer tw:transition-colors'

const tabSizeMap: Record<TabSize, string> = {
  medium: 'tw:h-[42px] tw:typo-text-m',
  large: 'tw:h-[48px] tw:typo-text-l'
}

const tabClass = computed(() => [
  tabBaseClass,
  tabSizeMap[props.size],
  props.disabled
    ? 'tw:font-medium tw:text-usdg-color-text-subtle1 tw:opacity-30 tw:cursor-not-allowed'
    : isActive.value
      ? 'tw:font-semibold tw:text-usdg-color-text-title'
      : 'tw:font-medium tw:text-usdg-color-text-subtle1',
  tabListContext?.stretch.value ? 'tw:flex-1' : '',
  props.customClass
])
</script>
